/**
 * AdminTimelineDialog — minimal read-only chronological timeline viewer
 * for per-user and per-alias Activity Backbone timelines.
 *
 * Phase 5: leverages GET /api/admin/users/{user_id}/timeline and
 * GET /api/admin/aliases/{alias_id}/timeline. Rows render the normalized
 * canonical_event_type + actor/target summary — intentionally compact
 * and support-oriented; full filtering/search lives in the Activity Log.
 *
 * Phase 5.1: per-row checkbox selection + "Copy as support note" markdown.
 * Selection is optional: if nothing is selected, the copy action exports
 * all currently visible events. No backend change.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Clock, ArrowRight, RefreshCcw, Copy, Check } from "lucide-react";

const PAGE_SIZE = 50;

const fmtTs = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
};

// ISO-style compact timestamp for markdown export: "2026-04-20 14:02"
const fmtTsIso = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return iso; }
};

const humanizeEvent = (s) => String(s || "").replace(/_/g, " ");

// Stable key per event — prefers v3 event_id, falls back to ts+action combo.
const rowKey = (log, idx) =>
  log.event_id || `${log.timestamp || ""}|${log.action || log.event_type || ""}|${idx}`;

const statusClass = (status) => {
  if (status === "failed" || status === "blocked" || status === "denied") {
    return "border-red-200 text-red-600 bg-red-50";
  }
  if (status === "pending") return "border-amber-200 text-amber-600 bg-amber-50";
  if (status === "verified") return "border-emerald-200 text-emerald-600 bg-emerald-50";
  return "border-[#E5E5E5] text-[#555555] bg-[#F5F5F5]";
};

// ── Support-note markdown generator ────────────────────────────────────
// Produces a compact, ticket-friendly summary. Format:
//   ## {User|Alias} Timeline Summary
//   [Alias: <address>]         (alias scope only)
//   - [YYYY-MM-DD HH:MM] canonical_event_type — human label (k1: v1, k2: v2)
//
// Events are emitted chronologically (oldest → newest) so the note reads
// naturally top-to-bottom regardless of the dialog's display sort.
const META_KEYS_IN_ORDER = [
  "reason", "sender", "from", "to", "subject",
  "alias_address", "address", "plan_id",
];

function buildMetaSuffix(log) {
  const md = log.metadata || {};
  const parts = [];
  for (const k of META_KEYS_IN_ORDER) {
    const v = md[k];
    if (v !== undefined && v !== null && v !== "") {
      const str = String(v).replace(/\s+/g, " ").trim();
      if (str) parts.push(`${k}: ${str.length > 80 ? `${str.slice(0, 77)}…` : str}`);
    }
  }
  if (log.status && log.status !== "success") parts.push(`status: ${log.status}`);
  return parts.length ? ` (${parts.join(", ")})` : "";
}

function buildHumanLabel(log) {
  const canonical = log.canonical_event_type || log.event_type || log.action || "event";
  const human = humanizeEvent(canonical);
  // Prefer a concrete target label when present, otherwise just the humanized event name.
  const target = log.target?.label || log.target?.label_short || "";
  if (target && !target.startsWith("(")) return `${human}: ${target}`;
  return human.charAt(0).toUpperCase() + human.slice(1);
}

function buildSupportNote({ scope, title, logs, includeLinks = false, baseUrl = "" }) {
  const header = scope === "alias" ? "## Alias Timeline Summary" : "## User Timeline Summary";
  const aliasLine = scope === "alias" && title ? `Alias: ${title}` : "";
  // Clone + sort ascending (oldest first) for readability.
  const ordered = [...logs].sort((a, b) => {
    const ta = new Date(a.timestamp || 0).getTime();
    const tb = new Date(b.timestamp || 0).getTime();
    return ta - tb;
  });
  const lines = [];
  ordered.forEach((log) => {
    const canonical = log.canonical_event_type || log.event_type || log.action || "event";
    lines.push(`- [${fmtTsIso(log.timestamp)}] ${canonical} — ${buildHumanLabel(log)}${buildMetaSuffix(log)}`);
    if (includeLinks && log.event_id) {
      lines.push(`  → ${baseUrl}/housekeeping/activity?event_id=${log.event_id}`);
    }
  });
  const parts = [header];
  if (aliasLine) parts.push(aliasLine);
  parts.push(...lines);
  return parts.join("\n");
}

function TimelineRow({ log, idx, selected, onToggle }) {
  const canonical = log.canonical_event_type || log.event_type || log.action || "event";
  const actorLabel = log.actor?.label_short || log.actor?.label || log.actor?.id || "";
  const targetLabel = log.target?.label_short || log.target?.label || log.target?.id || "";
  const status = log.status || "success";
  const category = log.category || "";
  const key = rowKey(log, idx);

  return (
    <div
      className={`flex items-start gap-2 px-3 py-2.5 border-b border-[#E5E5E5] last:border-b-0 hover:bg-[#FAFAFA] ${selected ? "bg-blue-50/60" : ""}`}
      data-testid={`timeline-row-${key}`}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={() => onToggle(key)}
        className="mt-0.5 shrink-0"
        data-testid={`timeline-row-checkbox-${key}`}
        aria-label="Select event for support note"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[11px] font-mono text-[#111111] truncate" title={canonical}>
            {humanizeEvent(canonical)}
          </span>
          <Badge variant="outline" className={`text-[9px] font-mono px-1.5 py-0 ${statusClass(status)}`}>
            {status}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[#888888] flex-wrap">
          <span className="flex items-center gap-0.5"><Clock size={9} />{fmtTs(log.timestamp)}</span>
          {category && (
            <Badge variant="outline" className="text-[9px] font-mono px-1 py-0 border-[#E5E5E5] text-[#888888]">
              {category}
            </Badge>
          )}
          {actorLabel && (
            <span className="font-mono text-[#555555] truncate max-w-[160px]" title={actorLabel}>{actorLabel}</span>
          )}
          {targetLabel && (
            <>
              <ArrowRight size={9} className="text-[#AAAAAA]" />
              <span className="font-mono text-[#555555] truncate max-w-[160px]" title={targetLabel}>{targetLabel}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminTimelineDialog({ open, onOpenChange, scope, scopeId, title, subtitle }) {
  // scope: "user" | "alias"
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [copied, setCopied] = useState(false);
  const [includeLinks, setIncludeLinks] = useState(false);

  const endpoint = scope === "alias"
    ? `/admin/aliases/${scopeId}/timeline`
    : `/admin/users/${scopeId}/timeline`;

  const fetchPage = useCallback(async (reset = false) => {
    if (!scopeId) return;
    setLoading(true);
    setError(null);
    try {
      const nextSkip = reset ? 0 : skip;
      const { data } = await api.get(endpoint, {
        params: { skip: nextSkip, limit: PAGE_SIZE, sort: "desc" },
      });
      setTotal(data.total || 0);
      if (reset) {
        setLogs(data.logs || []);
        setSkip(PAGE_SIZE);
      } else {
        setLogs((prev) => [...prev, ...(data.logs || [])]);
        setSkip(nextSkip + PAGE_SIZE);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [endpoint, scopeId, skip]);

  useEffect(() => {
    if (open && scopeId) {
      setLogs([]);
      setSkip(0);
      setTotal(0);
      setSelected(new Set());
      setCopied(false);
      fetchPage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeId]);

  const hasMore = logs.length < total;

  const toggleRow = useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectedCount = selected.size;

  // Map of key → log for O(1) lookup when exporting.
  const keyedLogs = useMemo(() => {
    const m = new Map();
    logs.forEach((log, idx) => m.set(rowKey(log, idx), log));
    return m;
  }, [logs]);

  const clearSelection = () => setSelected(new Set());

  const handleCopy = useCallback(async () => {
    // Selected subset if any, otherwise the visible batch.
    let target;
    if (selectedCount > 0) {
      target = [];
      selected.forEach((k) => {
        const l = keyedLogs.get(k);
        if (l) target.push(l);
      });
    } else {
      target = logs;
    }
    if (!target.length) {
      toast.error("Nothing to copy");
      return;
    }
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
    const note = buildSupportNote({ scope, title, logs: target, includeLinks, baseUrl });
    try {
      await navigator.clipboard.writeText(note);
      setCopied(true);
      toast.success(`Copied ${target.length} event${target.length === 1 ? "" : "s"} to clipboard`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard unavailable — copy blocked by browser");
    }
  }, [selectedCount, selected, keyedLogs, logs, scope, title, includeLinks]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] p-0" data-testid={`timeline-dialog-${scope}`}>
        <div className="max-h-[85vh] flex flex-col">
          <div className="p-6 pb-3 border-b border-[#E5E5E5]">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                Timeline
                <Badge variant="outline" className="text-[10px] font-mono capitalize">{scope}</Badge>
                {total > 0 && (
                  <span className="text-xs text-[#888888] font-mono" data-testid="timeline-total">{total} events</span>
                )}
              </DialogTitle>
              <DialogDescription className="text-xs font-mono text-[#888888] truncate">
                {title}
                {subtitle && <span className="ml-2 text-[#AAAAAA]">{subtitle}</span>}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto">
            {error && (
              <div className="p-4 text-xs text-red-600 bg-red-50 border-b border-red-100" data-testid="timeline-error">
                {error}
              </div>
            )}
            {logs.length === 0 && !loading && !error && (
              <div className="p-8 text-center text-xs text-[#888888]" data-testid="timeline-empty">
                No activity recorded yet.
              </div>
            )}
            {logs.map((log, idx) => {
              const k = rowKey(log, idx);
              return (
                <TimelineRow
                  key={k}
                  log={log}
                  idx={idx}
                  selected={selected.has(k)}
                  onToggle={toggleRow}
                />
              );
            })}
            {loading && (
              <div className="p-4 flex items-center justify-center text-xs text-[#888888] gap-2">
                <Loader2 size={12} className="animate-spin" /> Loading...
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#E5E5E5] flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-8"
              onClick={() => fetchPage(true)}
              disabled={loading}
              data-testid="timeline-refresh"
            >
              <RefreshCcw size={12} className="mr-1.5" /> Refresh
            </Button>
            {hasMore && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-8"
                onClick={() => fetchPage(false)}
                disabled={loading}
                data-testid="timeline-load-more"
              >
                Load more ({total - logs.length} left)
              </Button>
            )}
            <div className="flex-1" />
            {selectedCount > 0 && (
              <button
                className="text-[11px] text-[#888888] hover:text-[#555555] underline-offset-2 hover:underline"
                onClick={clearSelection}
                data-testid="timeline-clear-selection"
              >
                Clear ({selectedCount})
              </button>
            )}
            <label
              className="flex items-center gap-1.5 text-[11px] text-[#555555] cursor-pointer select-none"
              title="Append a link back to each event in the copied markdown"
              data-testid="timeline-include-links-label"
            >
              <Checkbox
                checked={includeLinks}
                onCheckedChange={(v) => setIncludeLinks(!!v)}
                data-testid="timeline-include-links"
                aria-label="Include links to events"
              />
              Include links
            </label>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={handleCopy}
              disabled={loading || logs.length === 0}
              title={selectedCount > 0
                ? `Copy ${selectedCount} selected event${selectedCount === 1 ? "" : "s"} as markdown`
                : "Copy all visible events as markdown"}
              data-testid="timeline-copy-note"
            >
              {copied ? (
                <Check size={12} className="mr-1.5 text-emerald-600" />
              ) : (
                <Copy size={12} className="mr-1.5" />
              )}
              Copy as support note{selectedCount > 0 ? ` (${selectedCount})` : ""}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-8 text-[#888888]"
              onClick={() => onOpenChange(false)}
              data-testid="timeline-close"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
